import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LogOut, Moon, Settings as SettingsIcon, ShieldCheck, Sun, User as UserIcon } from "lucide-react";
import { AnchoredMenu } from "@/components/ui/anchored-menu";
import { useAuthStore } from "@/store/useAuthStore";
import { logout as apiLogout } from "@/api/auth";
import { apiFetch } from "@/api/client";
import { SETTINGS_ROUTE_ENABLED } from "@/lib/settingsConfig";

const THEME_STORAGE_KEY = "kormax-theme";

const CRMOLD_BASE = "https://crmold.kormaxtrade.com.ua";

interface AdminBootstrapResponse {
  ok: boolean;
  code: string;
}

/**
 * SSO bridge to the stock Espo admin (crmold). Mints a single-use code, then
 * opens crmold/?ssoBootstrap=CODE#Admin where the crmold shim exchanges it for a
 * session and skips the login form. The tab is opened synchronously (preserving
 * the click's user-activation so popup blockers don't fire), then navigated once
 * the code is ready. On any failure we fall through to the plain admin URL where
 * the user logs in manually.
 */
function openAdminBridge(): void {
  // Synchronous open keeps the user gesture; navigate after the async bootstrap.
  const win = window.open("about:blank", "_blank");

  void (async () => {
    let url = `${CRMOLD_BASE}/#Admin`;
    try {
      const res = await apiFetch<AdminBootstrapResponse>(
        "/Custom/frontend/auth/admin-session-bootstrap",
        { method: "POST" },
      );
      if (res?.ok && res.code) {
        url = `${CRMOLD_BASE}/?ssoBootstrap=${encodeURIComponent(res.code)}#Admin`;
      }
    } catch {
      /* fall through — user logs in manually on crmold */
    }

    if (win && !win.closed) {
      win.location.replace(url);
    } else {
      // Popup was blocked or closed — last resort.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  })();
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function applyTheme(theme: "light" | "dark") {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

function readStoredTheme(): "light" | "dark" {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function writeStoredTheme(theme: "light" | "dark"): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* storage blocked — theme is per-session only */
  }
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const resetSession = useAuthStore((s) => s.reset);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [theme, setTheme] = useState<"light" | "dark">(readStoredTheme);

  const handleLogout = async () => {
    await apiLogout();
    resetSession();
    queryClient.removeQueries({ queryKey: ["me"] });
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    applyTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Shared menu-item styling (mirrors the former DropdownMenuItem look).
  const itemClass =
    "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none " +
    "hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none";

  return (
    <AnchoredMenu
      align="end"
      menuLabel="Меню користувача"
      className="w-60 p-1"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Меню користувача"
        >
          {user ? getInitials(user.name) : "?"}
        </button>
      )}
    >
      {({ close }) => (
        <>
          {user && (
            <>
              <div className="px-2 py-1.5 text-sm font-semibold text-foreground">{user.name}</div>
              {user.email && (
                <div className="px-2 pb-1 text-xs text-muted-foreground">{user.email}</div>
              )}
              {user.roles.length > 0 && (
                <div className="px-2 pb-2 text-xs text-muted-foreground">
                  {user.roles.join(", ")}
                </div>
              )}
              <div className="-mx-1 my-1 h-px bg-border" />
            </>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              toggleTheme();
              close();
            }}
            className={itemClass}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? "Світла тема" : "Темна тема"}
          </button>
          {/* Profile/Settings entry. Re-wired to the SPA /settings shell
              (TZ_Settings_Home v1.1). Rollback: flip SETTINGS_ROUTE_ENABLED →
              reverts to the crmold Espo profile link below. */}
          {SETTINGS_ROUTE_ENABLED ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                navigate("/settings");
              }}
              className={itemClass}
            >
              <SettingsIcon className="w-4 h-4" />
              Налаштування
            </button>
          ) : (
            <a href="/#User/view/me" role="menuitem" className={itemClass} onClick={close}>
              <UserIcon className="w-4 h-4" />
              Профіль (Espo)
            </a>
          )}
          {user?.type === "admin" && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                openAdminBridge();
              }}
              className={itemClass}
            >
              <ShieldCheck className="w-4 h-4" />
              Адмін-панель (Espo)
            </button>
          )}
          <div className="-mx-1 my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              void handleLogout();
            }}
            className={itemClass + " text-destructive hover:text-destructive focus-visible:text-destructive"}
          >
            <LogOut className="w-4 h-4" />
            Вийти
          </button>
        </>
      )}
    </AnchoredMenu>
  );
}
